import { Link, useRouter } from 'expo-router';
import { useQuery } from 'urql';
import * as Placeholder from '@/components/Custom/Placeholder';
import ActionBar from '@/components/UI/ActionBar';
import ActionButton from '@/components/UI/ActionButton';
import Text from '@/components/UI/Text';
import DepthListItem from '@/components/Unistyles/DepthListItem';
import List from '@/components/Unistyles/List';
import Pressable from '@/components/Unistyles/Pressable';
import { graphql } from '@/gql/graphql';

const GetEventDetailSchedules = graphql(/* GraphQL */ `
  query GetEventDetailSchedules($eventId: uuid!) {
    event_by_pk(id: $eventId) {
      id
      title
      permissions
    }
    event(
      where: {
        is_group: { _eq: true }
        ancestry: {
          _and: [
            {
              event_id: { _neq: $eventId }
              ancestor_ids: { _contains: [$eventId] }
            }
          ]
        }
      }
      order_by: { ancestry: { title: asc } }
      limit: 250
    ) {
      id
      title
      parent_id
    }
  }
`);

type EventDetailSchedulesProps = {
  basePath: string;
  eventId: string;
};

export default function EventDetailSchedules({
  basePath,
  eventId,
}: EventDetailSchedulesProps) {
  const { push } = useRouter();

  const [{ data, fetching }] = useQuery({
    query: GetEventDetailSchedules,
    variables: { eventId },
    requestPolicy: 'cache-and-network',
  });

  const events = data?.event || [];

  return (
    <>
      {data?.event_by_pk?.permissions?.create_event ? (
        <ActionBar>
          <ActionButton
            forceLabel
            label="Create Schedule"
            icon="add-outline"
            variant="success"
            onPress={() => {
              push({
                pathname: '/event/create',
                params: { parentId: eventId, eventType: 'group' },
              });
            }}
          />
        </ActionBar>
      ) : null}

      <List
        fetching={fetching}
        data={events}
        keyExtractor={(item) => item.id}
        renderItem={({ item, index }) => {
          return (
            <Link
              asChild
              href={{ pathname: basePath, params: { eventId: item.id } }}
              push
            >
              <Pressable>
                <DepthListItem
                  items={events}
                  index={index}
                  getId={(event) => event.id}
                  getParentId={(event) => event.parent_id}
                >
                  <Text numberOfLines={1} size="lg">
                    {item.title}
                  </Text>
                </DepthListItem>
              </Pressable>
            </Link>
          );
        }}
        ListEmptyComponent={
          <Placeholder.Container>
            <Placeholder.Icon name="trophy-outline" />
            <Placeholder.Text>
              There are no Schedules grouped under {data?.event_by_pk?.title}.
            </Placeholder.Text>
            <Placeholder.Text>
              Schedules usually function as a League or Season, and you can have
              Schedules inside of Schedules if you need to.
            </Placeholder.Text>
          </Placeholder.Container>
        }
      />
    </>
  );
}
